import { useEffect, useState } from "react";
import { Navigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { CheckingCredentials, useAuth } from "../../auth";
import { useAuthApiClient } from "../../hooks";
import { ROUTES } from "../../routes";
import { LoginForm } from "./LoginForm";
import { SecondFactorForm } from "./SecondFactorForm";

export const TwoStepLogin = () => {
    const authApiClient = useAuthApiClient();

    // Username and password states
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [intermediateToken, setIntermediateToken] = useState("");
    const [deviceType, setDeviceType] = useState("");
    const [challengeResponse, setChallengeResponse] = useState("");

    // Form control states
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [loginState, setLoginState] = useState<"ssocheck" | "login" | "2fa">(
        "ssocheck",
    );

    // Session state. The check runs on mount, nothing to kick off here.
    const {
        loading: globalLoading,
        rechecking: sessionRechecking,
        loggedIn: isLoggedIn,
        error: sessionCheckFailed,
        refresh: recheckSession,
    } = useAuth();

    // Handle next parameter
    const [searchParams] = useSearchParams();
    const nextParam = searchParams.get("next");

    const performLogin = async () => {
        try {
            setError("");
            setLoading(true);
            const response = await authApiClient.authLoginCreate({
                loginViewRequest: {
                    email: email,
                    password: password,
                },
            });
            setIntermediateToken(response.intermediateToken);
            setDeviceType(response.deviceType);
            setLoginState("2fa");
            setLoading(false);
        } catch {
            setError("Incorrect username or password.");
            setLoading(false);
        }
    };

    const performSSOCheck = async () => {
        try {
            setError("");
            setLoading(true);
            const response = await authApiClient.authSsoRequiredCreate({
                sSORequiredCheckRequest: {
                    email: email,
                    next: nextParam,
                },
            });
            if (response.ssoRequired) {
                const url = response.authUrl;
                window.location.href = url;
            } else {
                setLoginState("login");
                setLoading(false);
            }
        } catch {
            setError("Incorrect username.");
            setLoading(false);
        }
    };

    const performSecondFactorCheck = async () => {
        try {
            setLoading(true);
            await authApiClient.auth2faCreate({
                secondFactorViewRequest: {
                    challengeResponse,
                    intermediateToken,
                },
            });
            // The session cookie is set now, so re-run the check to pick it up.
            // On success isLoggedIn flips and we redirect below; on failure say
            // so rather than leaving the form spinning forever.
            const session = await recheckSession();
            if (session.isError) {
                setError(
                    "We could not confirm your session. Please try again.",
                );
                setLoading(false);
            }
        } catch {
            setError("Incorrect second factor token.");
            setLoading(false);
        }
    };

    // Clear localStorage when the login screen is shown to an unauthenticated
    // user, preserving only the cookie banner consent. A failed session check
    // is what tells us the user is definitely un-authenticated.
    useEffect(() => {
        if (sessionCheckFailed) {
            const cookiesAccepted = localStorage.getItem("cookiesAccepted");
            localStorage.clear();
            if (cookiesAccepted) {
                localStorage.setItem("cookiesAccepted", cookiesAccepted);
            }
        }
    }, [sessionCheckFailed]);

    // Redirect to dashboard if logged in
    if (isLoggedIn) {
        let nextPage = ROUTES.ROOT;
        if (nextParam) {
            nextPage = nextParam;
        }
        return <Navigate to={nextPage} />;
    }

    // Display a loading page instead of flashing login page. Covers both the
    // first check and the re-check after 2FA, which is a refetch rather than an
    // initial load.
    if (globalLoading || sessionRechecking) {
        return <CheckingCredentials />;
    }

    return (
        <>
            {(loginState === "login" || loginState === "ssocheck") && (
                <LoginForm
                    email={email}
                    onChangeEmail={setEmail}
                    password={password}
                    onChangePassword={setPassword}
                    submit={
                        loginState === "login" ? performLogin : performSSOCheck
                    }
                    loading={loading}
                    error={error}
                    ssoCheck={loginState === "ssocheck"}
                />
            )}
            {loginState === "2fa" && (
                <SecondFactorForm
                    challengeResponse={challengeResponse}
                    onChangeChallengeResponse={setChallengeResponse}
                    device={deviceType}
                    performLogin={performSecondFactorCheck}
                    loading={loading}
                    error={error}
                />
            )}
        </>
    );
};
